/**
 * @file ServiceKeys.ts
 * @role The typed handles used to register/resolve services in the ServiceContainer.
 *       Centralized so the composition root and any explicit consumer agree on types.
 * @owns The ServiceKey constants for v1 services.
 * @neverDoes Construct services. Bootstrapper builds and registers them.
 * @invariants 13.
 * @see docs/ARCHITECTURE.md
 */
import { createServiceKey } from './ServiceContainer';
import type { GameApplication } from './GameApplication';
import type { GameTicker } from './GameTicker';
import type { GameEnvironment } from './GameEnvironment';
import type { SceneDirector } from './SceneDirector';
import type { EventBus } from '@core/events/EventBus';
import type { LayoutService } from '@core/layout/LayoutService';
import type { AssetService } from '@core/assets/AssetService';
import type { GameClock } from '@core/time/GameClock';
import type { ConfigService } from '@core/config/ConfigService';
import type { UiConfig } from '@core/config/UiConfig';
import type { SettingsService } from '@core/settings/SettingsService';
import type { GraphicsQualityService } from '@core/graphics/GraphicsQualityService';
import type { AudioService } from '@core/audio/AudioService';
import type { AnalyticsService } from '@core/analytics/AnalyticsService';
import type { ParticleService } from '@core/particles/ParticleService';
import type { PostFxService } from '@core/postfx/PostFxService';
import type { AmbientFxService } from '@game/ambient/AmbientFxService';
import type { Logger } from '@core/logging/Logger';
import type { GameSkin } from './GameSkin';
import type { DiagnosticsService } from '@core/diagnostics/DiagnosticsService';

export const ServiceKeys = {
  GameApplication: createServiceKey<GameApplication>('GameApplication'),
  GameTicker: createServiceKey<GameTicker>('GameTicker'),
  GameClock: createServiceKey<GameClock>('GameClock'),
  GameEnvironment: createServiceKey<GameEnvironment>('GameEnvironment'),
  Logger: createServiceKey<Logger>('Logger'),
  EventBus: createServiceKey<EventBus>('EventBus'),
  LayoutService: createServiceKey<LayoutService>('LayoutService'),
  AssetService: createServiceKey<AssetService>('AssetService'),
  ConfigService: createServiceKey<ConfigService>('ConfigService'),
  UiConfig: createServiceKey<UiConfig>('UiConfig'),
  GameSkin: createServiceKey<GameSkin>('GameSkin'),
  SettingsService: createServiceKey<SettingsService>('SettingsService'),
  GraphicsQualityService: createServiceKey<GraphicsQualityService>('GraphicsQualityService'),
  AudioService: createServiceKey<AudioService>('AudioService'),
  AnalyticsService: createServiceKey<AnalyticsService>('AnalyticsService'),
  ParticleService: createServiceKey<ParticleService>('ParticleService'),
  PostFxService: createServiceKey<PostFxService>('PostFxService'),
  AmbientFxService: createServiceKey<AmbientFxService>('AmbientFxService'),
  DiagnosticsService: createServiceKey<DiagnosticsService>('DiagnosticsService'),
  SceneDirector: createServiceKey<SceneDirector>('SceneDirector'),
} as const;
